import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Image
} from '@react-pdf/renderer';
import PropTypes from 'prop-types';
import hyLogo from 'assets/img/logos/hy-logo.png';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 10
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    borderBottom: '2px solid #2c5282',
    paddingBottom: 15
  },
  logo: {
    width: 80,
    height: 80
  },
  studentInfoContainer: {
    flexDirection: 'row',
    gap: 15
  },
  profilePic: {
    width: 80,
    height: 80,
    borderRadius: 4,
    objectFit: 'cover'
  },
  studentDetails: {
    flex: 1
  },
  headerText: {
    textAlign: 'right'
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c5282'
  },
  subtitle: {
    fontSize: 12,
    color: '#4a5568',
    marginTop: 5
  },
  section: {
    marginBottom: 15
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    backgroundColor: '#edf2f7',
    padding: 8,
    marginBottom: 10,
    color: '#2d3748'
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5
  },
  label: {
    width: '35%',
    fontWeight: 'bold',
    color: '#4a5568'
  },
  value: {
    width: '65%',
    color: '#1a202c'
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#e2e8f0',
    padding: 8,
    fontWeight: 'bold'
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottom: '1px solid #e2e8f0'
  },
  tableCell: {
    flex: 1
  },
  tableCellWide: {
    flex: 2
  },
  textArea: {
    padding: 8,
    backgroundColor: '#f7fafc',
    borderRadius: 4,
    marginTop: 5,
    minHeight: 40
  },
  attendanceBox: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    padding: 10,
    backgroundColor: '#f7fafc',
    marginTop: 5
  },
  attendanceItem: {
    textAlign: 'center'
  },
  attendanceNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c5282'
  },
  attendanceLabel: {
    fontSize: 9,
    color: '#718096'
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTop: '1px solid #e2e8f0',
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#718096'
  }
});

const SchoolReportPDF = ({ data, beneficiary, organization }) => {
  const reportDate = data.reportDate
    ? new Date(data.reportDate).toLocaleDateString('en-GB')
    : new Date().toLocaleDateString('en-GB');

  // Build student name safely to avoid empty string issues
  const studentName = [
    beneficiary?.first_name,
    beneficiary?.middle_name,
    beneficiary?.last_name
  ].filter(Boolean).join(' ') || '-';

  const calculateAttendancePercentage = () => {
    const present = parseInt(data.daysPresent) || 0;
    const absent = parseInt(data.daysAbsent) || 0;
    const total = present + absent;
    if (total === 0) return '0';
    return ((present / total) * 100).toFixed(1);
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Image src={hyLogo} style={styles.logo} />
          <View style={styles.headerText}>
            <Text style={styles.title}>School Progress Report</Text>
            {organization?.name && (
              <Text style={styles.subtitle}>{organization.name}</Text>
            )}
          </View>
        </View>

        {/* Student Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Student Information</Text>
          <View style={styles.studentInfoContainer}>
            {beneficiary?.image && (
              <Image src={beneficiary.image} style={styles.profilePic} />
            )}
            <View style={styles.studentDetails}>
              <View style={styles.row}>
                <Text style={styles.label}>Student Name:</Text>
                <Text style={styles.value}>{studentName}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Beneficiary ID:</Text>
                <Text style={styles.value}>
                  {beneficiary?.simple_id ? `B${beneficiary.simple_id}` : '-'}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>School:</Text>
                <Text style={styles.value}>{data.schoolName || '-'}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Term/Period:</Text>
                <Text style={styles.value}>{data.term || '-'}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Report Date:</Text>
                <Text style={styles.value}>{reportDate}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Academic Performance */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Academic Performance</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Overall Performance:</Text>
            <Text style={styles.value}>{data.overallGrade || '-'}</Text>
          </View>

          {data.subjects && data.subjects.length > 0 && (
            <View style={{ marginTop: 10 }}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCell}>Subject</Text>
                <Text style={styles.tableCell}>Grade</Text>
                <Text style={styles.tableCellWide}>Comments</Text>
              </View>
              {data.subjects.map((subject, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{subject.name || '-'}</Text>
                  <Text style={styles.tableCell}>{subject.grade || '-'}</Text>
                  <Text style={styles.tableCellWide}>
                    {subject.comments || '-'}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Attendance */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Attendance</Text>
          <View style={styles.attendanceBox}>
            <View style={styles.attendanceItem}>
              <Text style={styles.attendanceNumber}>
                {data.daysPresent || 0}
              </Text>
              <Text style={styles.attendanceLabel}>Days Present</Text>
            </View>
            <View style={styles.attendanceItem}>
              <Text style={styles.attendanceNumber}>
                {data.daysAbsent || 0}
              </Text>
              <Text style={styles.attendanceLabel}>Days Absent</Text>
            </View>
            <View style={styles.attendanceItem}>
              <Text style={styles.attendanceNumber}>
                {calculateAttendancePercentage()}%
              </Text>
              <Text style={styles.attendanceLabel}>Attendance Rate</Text>
            </View>
          </View>
        </View>

        {/* Teacher Feedback */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Teacher Feedback</Text>
          {data.strengths && (
            <View style={{ marginBottom: 10 }}>
              <Text style={{ fontWeight: 'bold' }}>Strengths:</Text>
              <View style={styles.textArea}>
                <Text>{data.strengths}</Text>
              </View>
            </View>
          )}
          {data.areasForImprovement && (
            <View style={{ marginBottom: 10 }}>
              <Text style={{ fontWeight: 'bold' }}>Areas for Improvement:</Text>
              <View style={styles.textArea}>
                <Text>{data.areasForImprovement}</Text>
              </View>
            </View>
          )}
          {data.generalComments && (
            <View style={{ marginBottom: 10 }}>
              <Text style={{ fontWeight: 'bold' }}>General Comments:</Text>
              <View style={styles.textArea}>
                <Text>{data.generalComments}</Text>
              </View>
            </View>
          )}
        </View>

        {/* Additional Information */}
        {(data.extracurricular || data.healthNotes || data.recommendations) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Additional Information</Text>
            {data.extracurricular && (
              <View style={{ marginBottom: 10 }}>
                <Text style={{ fontWeight: 'bold' }}>
                  Extracurricular Activities:
                </Text>
                <View style={styles.textArea}>
                  <Text>{data.extracurricular}</Text>
                </View>
              </View>
            )}
            {data.healthNotes && (
              <View style={{ marginBottom: 10 }}>
                <Text style={{ fontWeight: 'bold' }}>
                  Health/Wellbeing Notes:
                </Text>
                <View style={styles.textArea}>
                  <Text>{data.healthNotes}</Text>
                </View>
              </View>
            )}
            {data.recommendations && (
              <View style={{ marginBottom: 10 }}>
                <Text style={{ fontWeight: 'bold' }}>Recommendations:</Text>
                <View style={styles.textArea}>
                  <Text>{data.recommendations}</Text>
                </View>
              </View>
            )}
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text>Generated: {new Date().toLocaleString('en-GB')}</Text>
          <Text>
            By: {data.reporterName || 'System'}{organization?.name ? ` | ${organization.name}` : ''}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

SchoolReportPDF.propTypes = {
  data: PropTypes.shape({
    reportDate: PropTypes.string,
    term: PropTypes.string,
    schoolName: PropTypes.string,
    overallGrade: PropTypes.string,
    subjects: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        grade: PropTypes.string,
        comments: PropTypes.string
      })
    ),
    daysPresent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    daysAbsent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    strengths: PropTypes.string,
    areasForImprovement: PropTypes.string,
    generalComments: PropTypes.string,
    extracurricular: PropTypes.string,
    healthNotes: PropTypes.string,
    recommendations: PropTypes.string,
    reporterName: PropTypes.string
  }).isRequired,
  beneficiary: PropTypes.object,
  organization: PropTypes.object
};

export default SchoolReportPDF;
